/**
 * Connections module hooks (v1.1-C3) — full real-data binding for the
 * /connections UI. Replaces every MOCK_MCP_SERVERS / MOCK_API_CONNECTIONS /
 * MOCK_ERROR_LOG read.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';
import type { McpCredentialsInput, McpDockerConfigInput } from '@argusai/shared/schemas/mcp-install.schema';
import type {
  McpServer,
  ApiConnection,
  ErrorLogEntry,
  McpTier,
  ConnectionStatus,
  CircuitBreakerState,
  AuthType,
} from './mock-connections-data';

export interface TestResult {
  ok: boolean;
  status: string;
  message: string;
  latencyMs: number | null;
  testedAt: string;
}

interface ServerMcpServer {
  id: string;
  systemServerId: string | null;
  displayName: string;
  description: string;
  namespace: string;
  tier: string;
  endpointUrl: string;
  status: string;
  isHealthy: boolean;
  // connector-status-truth (bugs #1/#2/#12/#14): backend-computed real
  // usability. Optional for back-compat with any cached/older payloads.
  connectionState?: 'connected' | 'needsSetup' | 'unavailable';
  isConnected?: boolean;
  needsSetupReason?: string;
  requiresAuth?: boolean;
  signedIn?: boolean;
  toolCount: number;
  circuitBreaker: {
    state: string;
    failures: number;
    threshold: number;
    lastFailure: string | null;
    resetTimeout: number;
  };
  docker: {
    image: string;
    tag: string;
    cpuLimit: string;
    memoryLimit: string;
    sandboxed: boolean;
  };
  metrics: {
    latencyMs: number;
    uptimePercent: number;
    requestsPerHour: number;
    errorRate: number;
  };
  credentials: { type: string; configured: boolean };
  tools: unknown[];
  lastHealthCheck: string | null;
  lastTestResult: TestResult | null;
  createdAt: string;
  updatedAt: string;
}

interface ServerApiConnection {
  id: string;
  displayName: string;
  description: string;
  baseUrl: string;
  specFormat: string;
  authType: string;
  status: string;
  isHealthy: boolean;
  // connector-status-truth — same as the MCP VM (apiToVm emits these).
  connectionState?: 'connected' | 'needsSetup' | 'unavailable';
  isConnected?: boolean;
  needsSetupReason?: string;
  endpointCount: number;
  endpoints: unknown[];
  auth: Record<string, unknown>;
  metrics: {
    latencyMs: number;
    uptimePercent: number;
    requestsPerHour: number;
    errorRate: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface ServerError {
  id: string;
  connectionKind: string;
  connectionId: string;
  connectionName: string | null;
  occurredAt: string;
  errorKind: string;
  statusCode: number | null;
  message: string;
}

/**
 * connector-status-truth (bugs #1/#2/#12/#14): the backend now reports the
 * REAL usability of a connector via `connectionState`/`isConnected` (see
 * computeMcpConnectionState / apiToVm on the API side). The legacy `status`
 * field is still derived purely from health_status, so an Outlook OAuth
 * install with no token reads `status:'online'` even though the runtime
 * cannot call it. Downgrade the DISPLAY status to match truth so the
 * dot/badge (cards, detail header, sheet) and the Health "Healthy"
 * aggregate stop over-reporting health.
 */
function deriveConnectionStatus(
  legacy: string,
  connectionState?: 'connected' | 'needsSetup' | 'unavailable',
  isConnected?: boolean,
): ConnectionStatus {
  if (connectionState === 'unavailable') return 'offline';
  if (connectionState === 'needsSetup') return 'degraded';
  if (isConnected === false) return 'degraded';
  return legacy as ConnectionStatus;
}

function adaptMcp(s: ServerMcpServer): McpServer & {
  systemServerId: string | null;
  lastHealthCheck: string | null;
  lastTestResult: TestResult | null;
} {
  return {
    id: s.id,
    systemServerId: s.systemServerId ?? null,
    displayName: s.displayName,
    description: s.description,
    namespace: s.namespace,
    tier: s.tier as McpTier,
    endpointUrl: s.endpointUrl,
    status: deriveConnectionStatus(s.status, s.connectionState, s.isConnected),
    isHealthy: s.isHealthy,
    // connector-status-truth (bug #2): carry the backend's real-usability
    // fields through to the card so it can render a "Sign in to use" /
    // "Setup required" affordance instead of dropping them. Pre-fix the
    // adapter only kept the derived legacy `status`, so the card had no
    // way to distinguish "added but needs sign-in" from "healthy".
    connectionState: s.connectionState,
    isConnected: s.isConnected,
    needsSetupReason: s.needsSetupReason as McpServer['needsSetupReason'],
    requiresAuth: s.requiresAuth,
    signedIn: s.signedIn,
    toolCount: s.toolCount,
    circuitBreaker: {
      state: s.circuitBreaker.state as CircuitBreakerState,
      failures: s.circuitBreaker.failures,
      threshold: s.circuitBreaker.threshold,
      lastFailure: s.circuitBreaker.lastFailure,
      resetTimeout: s.circuitBreaker.resetTimeout,
    },
    docker: s.docker,
    metrics: s.metrics,
    credentials: {
      type: s.credentials.type as McpServer['credentials']['type'],
      configured: s.credentials.configured,
    },
    tools: (s.tools as McpServer['tools']) ?? [],
    lastHealthCheck: s.lastHealthCheck,
    lastTestResult: s.lastTestResult,
    createdAt: s.createdAt,
    updatedAt: s.updatedAt,
  };
}

function adaptApi(s: ServerApiConnection): ApiConnection {
  return {
    id: s.id,
    displayName: s.displayName,
    description: s.description,
    baseUrl: s.baseUrl,
    specFormat: s.specFormat as ApiConnection['specFormat'],
    authType: s.authType as AuthType,
    status: deriveConnectionStatus(s.status, s.connectionState, s.isConnected),
    isHealthy: s.isHealthy,
    endpointCount: s.endpointCount,
    endpoints: (s.endpoints as ApiConnection['endpoints']) ?? [],
    auth: {
      type: s.authType as AuthType,
      configured:
        typeof s.auth.configured === 'boolean'
          ? (s.auth.configured as boolean)
          : s.authType !== 'none',
      headerName:
        typeof s.auth.headerName === 'string'
          ? (s.auth.headerName as string)
          : undefined,
      tokenUrl:
        typeof s.auth.tokenUrl === 'string'
          ? (s.auth.tokenUrl as string)
          : undefined,
      scopes:
        Array.isArray(s.auth.scopes) ? (s.auth.scopes as string[]) : undefined,
    },
    metrics: s.metrics,
    createdAt: s.createdAt,
    updatedAt: s.updatedAt,
  };
}

function adaptError(s: ServerError): ErrorLogEntry {
  const severity: ErrorLogEntry['severity'] =
    (s.statusCode ?? 0) >= 500 || s.errorKind === 'circuit_open'
      ? 'critical'
      : s.errorKind === 'auth' || s.errorKind === 'rate_limited'
      ? 'error'
      : 'warning';
  return {
    id: s.id,
    connectionId: s.connectionId,
    connectionName: s.connectionName ?? 'Unknown',
    connectionType: s.connectionKind as 'mcp' | 'api',
    timestamp: s.occurredAt,
    severity,
    message: s.message,
    details: s.statusCode ? `HTTP ${s.statusCode}` : undefined,
  };
}

export function useMcpServers() {
  return useQuery<{ servers: ServerMcpServer[] }, Error, McpServer[]>({
    queryKey: ['connections-module', 'mcp'],
    queryFn: () =>
      apiClient.get<{ servers: ServerMcpServer[] }>(
        '/connections-module/mcp',
      ),
    select: (r) => r.servers.map(adaptMcp),
  });
}

/**
 * #755 — accept an `enabled` override so the typed-alias detail
 * routes (`/connections/mcp/:id`, `/connections/api/:id`) can skip
 * the off-type lookup. Without the override both hooks fired and the
 * off-type fetch returned 404, polluting the browser console.
 */
export function useMcpServer(
  id: string | undefined,
  opts: { enabled?: boolean } = {},
) {
  return useQuery<ServerMcpServer, Error, McpServer | null>({
    queryKey: ['connections-module', 'mcp', id],
    enabled: !!id && opts.enabled !== false,
    queryFn: () =>
      apiClient.get<ServerMcpServer>(`/connections-module/mcp/${id}`),
    select: (r) => (r ? adaptMcp(r) : null),
  });
}

export function useApiConnections() {
  return useQuery<
    { connections: ServerApiConnection[] },
    Error,
    ApiConnection[]
  >({
    queryKey: ['connections-module', 'api'],
    queryFn: () =>
      apiClient.get<{ connections: ServerApiConnection[] }>(
        '/connections-module/api',
      ),
    select: (r) => r.connections.map(adaptApi),
  });
}

export function useApiConnection(
  id: string | undefined,
  opts: { enabled?: boolean } = {},
) {
  return useQuery<ServerApiConnection, Error, ApiConnection | null>({
    queryKey: ['connections-module', 'api', id],
    enabled: !!id && opts.enabled !== false,
    queryFn: () =>
      apiClient.get<ServerApiConnection>(`/connections-module/api/${id}`),
    select: (r) => (r ? adaptApi(r) : null),
  });
}

export function useCreateApiConnection() {
  const qc = useQueryClient();
  return useMutation<
    ServerApiConnection,
    Error,
    {
      displayName: string;
      description?: string;
      baseUrl: string;
      specFormat?: 'openapi3' | 'openapi2' | 'graphql';
      authType?: string;
      authConfig?: Record<string, unknown>;
      endpoints?: unknown[];
    }
  >({
    mutationFn: (body) =>
      apiClient.post<ServerApiConnection>('/connections-module/api', body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['connections-module', 'api'] }),
  });
}

export function useDeleteApiConnection() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiClient.delete<void>(`/connections-module/api/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['connections-module', 'api'] }),
  });
}

export function useConnectionErrors() {
  return useQuery<{ errors: ServerError[] }, Error, ErrorLogEntry[]>({
    queryKey: ['connections-module', 'errors'],
    queryFn: () =>
      apiClient.get<{ errors: ServerError[] }>('/connections-module/errors'),
    select: (r) => r.errors.map(adaptError),
  });
}

export interface MarketplaceServer {
  id: string;
  slug: string;
  displayName: string;
  description: string;
  icon: string;
  hostingMode: string;
  protocol: string;
  version: number;
  rating: number;
  reviewCount: number;
  installCount: number;
  author: string;
  license: string;
  marketplaceCategory: string;
  trustLevel: string;
  dockerImage: string | null;
  requiresAuth: boolean;
  /** Raw scheme from auth_scheme.scheme. Codex retest #160 (closes). */
  authType: string;
  /** User-facing label rendered by install dialog. Backend-derived. */
  authTypeLabel: string;
  marketplaceTags: string[];
}

export interface ServerCustomFunction {
  id: string;
  name: string;
  displayName: string;
  description: string;
  runtime: string;
  runtimeVersion: string;
  sideEffects: string[];
  source: string;
  sourceHash: string;
  networkHosts: string[];
  resourceLimits: Record<string, unknown>;
  state: string;
  activatedAt: string | null;
  disabledAt: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * True for published SYSTEM functions (authored by the platform admin,
   * org_id IS NULL) surfaced READ-ONLY in this tenant's list. `source` is
   * redacted for these rows and the UI must disable edit/deploy/delete.
   */
  isSystem?: boolean;
}

export interface CreateTenantFunctionInput {
  name: string;
  description: string;
  runtime: 'typescript' | 'python';
  source: string;
  // function_helper depth fix — the full definition the builder canvas produces
  // (optional; backend defaults to empty when absent). Schemas are JSON strings.
  inputSchemaJson?: string;
  outputSchemaJson?: string;
  sideEffects?: string[];
  networkHosts?: string[];
}

export interface ConnectionsCapabilities {
  /** The tenant's "Allow external connections" setting (SECC-T2) — the one switch. */
  externalConnectionsAllowed: boolean;
  adapters: {
    restApi: { enabled: boolean; reason: string };
    graphqlApi: { enabled: boolean; reason: string };
    mcpTier2: { enabled: boolean; reason: string };
    mcpTier3: { enabled: boolean; reason: string };
    marketplaceInstall: { enabled: boolean; reason: string };
  };
}

export function useConnectionsCapabilities() {
  return useQuery<ConnectionsCapabilities>({
    queryKey: ['connections-module', 'capabilities'],
    queryFn: () =>
      apiClient.get<ConnectionsCapabilities>('/connections-module/capabilities'),
    retry: false,
    staleTime: 60_000,
  });
}

export function useTenantFunctions() {
  return useQuery<{ functions: ServerCustomFunction[] }, Error, ServerCustomFunction[]>({
    queryKey: ['connections-module', 'functions'],
    queryFn: () =>
      apiClient.get<{ functions: ServerCustomFunction[] }>(
        '/connections-module/functions',
      ),
    select: (r) => r.functions,
  });
}

export function useCreateTenantFunction() {
  const qc = useQueryClient();
  return useMutation<ServerCustomFunction, Error, CreateTenantFunctionInput>({
    mutationFn: (body) =>
      apiClient.post<ServerCustomFunction>('/connections-module/functions', body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['connections-module', 'functions'] }),
  });
}

/**
 * Edit a custom function's source. Returns the function to DRAFT state
 * (signature invalidated; re-deploy republishes). Backend rejects active
 * functions with 409 FUNCTION_ACTIVE — disable before editing.
 */
export function useSaveFunctionSource() {
  const qc = useQueryClient();
  return useMutation<ServerCustomFunction, Error, { id: string; source: string }>({
    mutationFn: ({ id, source }) =>
      apiClient.patch<ServerCustomFunction>(
        `/connections-module/functions/${id}`,
        { source },
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['connections-module', 'functions'] }),
  });
}

/**
 * Per-input sandbox test result for a custom function. Mirrors the
 * /connections-module/functions/:id/test response envelope.
 */
export interface FunctionTestPerInput {
  input: unknown;
  output?: unknown;
  error?: { code: string; message: string };
  latencyMs: number;
}
export interface FunctionTestResult {
  runtime: string;
  results: FunctionTestPerInput[];
  allPassed: boolean;
}

/**
 * Run a tenant function's PERSISTED source in the production isolated-vm
 * sandbox against caller-supplied sample inputs. Studio bug #2 / Connections
 * #9 — replaces the static "Test unavailable" message with a real sandbox
 * invocation. Egress is denied (networkHosts=[]) for the test path; each
 * input is capped at 3s. Only runtime='ts' is runnable (422 otherwise).
 */
export function useTestFunction() {
  return useMutation<
    FunctionTestResult,
    Error,
    { id: string; sampleInputs: unknown[]; params?: Record<string, unknown> }
  >({
    mutationFn: ({ id, sampleInputs, params }) =>
      apiClient.post<FunctionTestResult>(
        `/connections-module/functions/${id}/test`,
        { sampleInputs, params },
      ),
  });
}

export function useMcpMarketplace() {
  return useQuery<{ servers: MarketplaceServer[] }, Error, MarketplaceServer[]>({
    queryKey: ['connections-module', 'marketplace'],
    queryFn: () =>
      apiClient.get<{ servers: MarketplaceServer[] }>(
        '/connections-module/marketplace',
      ),
    select: (r) => r.servers,
  });
}

// ─── Mutations: real backend actions ─────────────────────────────────

export function useTestMcpServer() {
  const qc = useQueryClient();
  return useMutation<TestResult, Error, string>({
    mutationFn: (id) =>
      apiClient.post<TestResult>(`/connections-module/mcp/${id}/test`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['connections-module', 'mcp'] });
      qc.invalidateQueries({ queryKey: ['connections-module', 'errors'] });
    },
  });
}

export function useResetMcpCircuitBreaker() {
  const qc = useQueryClient();
  return useMutation<ServerMcpServer, Error, string>({
    mutationFn: (id) =>
      apiClient.post<ServerMcpServer>(
        `/connections-module/mcp/${id}/reset-circuit-breaker`,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['connections-module', 'mcp'] }),
  });
}

export function useUninstallMcpServer() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete<void>(`/connections-module/mcp/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['connections-module', 'mcp'] }),
  });
}

export interface RegisterMcpInput {
  systemServerId?: string;
  slug?: string;
  displayName?: string;
  description?: string;
  /**
   * SECC-T4 — the REQUEST tier. Tier 3 is refused by the api (400
   * UNSUPPORTED_TIER) and Tier 1 is platform-provisioned (403), so a tenant
   * registers Tier 2; the read model keeps tier3 for catalogue rows.
   */
  tier: 'tier1' | 'tier2';
  endpointUrl: string;
  /** The shared schema's types — the api validates this body against it (McpTenantConfigInputSchema). */
  docker?: McpDockerConfigInput;
  credentials?: McpCredentialsInput;
}

export function useRegisterMcpServer() {
  const qc = useQueryClient();
  return useMutation<ServerMcpServer, Error, RegisterMcpInput>({
    mutationFn: (body) =>
      apiClient.post<ServerMcpServer>('/connections-module/mcp', body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['connections-module', 'mcp'] }),
  });
}

export function useTestApiConnection() {
  const qc = useQueryClient();
  return useMutation<TestResult, Error, string>({
    mutationFn: (id) =>
      apiClient.post<TestResult>(`/connections-module/api/${id}/test`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['connections-module', 'api'] });
      qc.invalidateQueries({ queryKey: ['connections-module', 'errors'] });
    },
  });
}

export function useToggleApiEndpoint() {
  const qc = useQueryClient();
  return useMutation<
    ServerApiConnection,
    Error,
    { connectionId: string; endpointId: string; enabled: boolean }
  >({
    mutationFn: ({ connectionId, endpointId, enabled }) =>
      apiClient.patch<ServerApiConnection>(
        `/connections-module/api/${connectionId}/endpoints/${endpointId}`,
        { enabled },
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['connections-module', 'api'] });
      qc.invalidateQueries({
        queryKey: ['connections-module', 'api', vars.connectionId],
      });
    },
  });
}

export function useDeployFunction() {
  const qc = useQueryClient();
  return useMutation<ServerCustomFunction, Error, string>({
    mutationFn: (id) =>
      apiClient.post<ServerCustomFunction>(
        `/connections-module/functions/${id}/deploy`,
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['connections-module', 'functions'] }),
  });
}

export function useDisableFunction() {
  const qc = useQueryClient();
  return useMutation<
    ServerCustomFunction,
    Error,
    { id: string; reason?: string }
  >({
    // Codex retest #240 — always send a JSON object, even when no
    // reason is supplied. The previous `body: undefined` shape paired
    // with the route's body schema flunked Fastify validation and
    // surfaced a generic "Validation failed" toast. Sending an empty
    // `{}` gives the validator a concrete value to accept and lets
    // the disable lifecycle complete.
    mutationFn: ({ id, reason }) =>
      apiClient.post<ServerCustomFunction>(
        `/connections-module/functions/${id}/disable`,
        reason ? { reason } : {},
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['connections-module', 'functions'] }),
  });
}

export function useDeleteFunction() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiClient.delete<void>(`/connections-module/functions/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['connections-module', 'functions'] }),
  });
}

// ── Meeting Bot connection type (bMTG) ──────────────────────────────────────

export interface MeetingBotConnection {
  id: string;
  organizationId: string;
  displayName: string;
  platform: string;
  vexaInstanceUrl: string;
  m365Config: {
    azureAdTenantId: string;
    appId: string | null;
    certThumbprint: string | null;
    certPrivateKeyRef: string | null;
    botMailboxUpn: string;
    webhookUrl: string | null;
    authMode: string;
    graphPermissionsConsented: string[];
    consentedAt: string | null;
    botDisplayName: string | null;
    botAvatarUrl: string | null;
  };
  vexaConfig: {
    vexaInstanceUrl: string;
    maxConcurrentMeetingsForTenant: number;
    sttEndpoint: string;
    ttsEndpoint: string;
    autoJoinPolicy: string;
  };
  meetingConfig: {
    wakeWord: {
      primary: string[];
      secondary: string[];
      confidenceThreshold: number;
    };
    tts: { mode: string; piperVoice: string | null; xttsVoiceConsentRef: string | null };
    contextWindow: {
      transcriptMinutesRetained: number;
      topicRefreshSeconds: number;
      prewarmRefreshSeconds: number;
    };
    maxConcurrentMeetings: number;
    requireExplicitFloorGrant: boolean;
  };
  isActive: boolean;
  healthStatus: string;
  lastTestResult: {
    ok: boolean;
    status: string;
    message: string;
    latencyMs: number | null;
    testedAt: string;
    steps?: Array<{ name: string; ok: boolean; message: string; latencyMs: number | null }>;
  } | null;
  circuitBreaker: {
    state: string;
    failures: number;
    threshold: number;
    lastFailure: string | null;
    resetTimeout: number;
  };
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMeetingBotConnectionInput {
  displayName: string;
  platform?: 'teams' | 'meet' | 'zoom';
  vexaInstanceUrl?: string;
  m365Config: {
    azureAdTenantId: string;
    appId?: string | null;
    botMailboxUpn: string;
    webhookUrl?: string | null;
    credential: string;
    authMode: 'password' | 'oauth_refresh_token';
    graphPermissionsConsented?: string[];
    consentedAt?: string | null;
    botDisplayName?: string | null;
    botAvatarUrl?: string | null;
  };
  vexaConfig: {
    vexaInstanceUrl?: string;
    maxConcurrentMeetingsForTenant?: number;
    sttEndpoint?: string;
    ttsEndpoint?: string;
    autoJoinPolicy?: 'invited_only' | 'all_meetings' | 'manual_only';
  };
  meetingConfig: {
    wakeWord?: { primary?: string[]; secondary?: string[]; confidenceThreshold?: number };
    tts?: { mode?: 'piper' | 'xtts'; piperVoice?: string | null };
    contextWindow?: {
      transcriptMinutesRetained?: number;
      topicRefreshSeconds?: number;
      prewarmRefreshSeconds?: number;
    };
    maxConcurrentMeetings?: number;
    requireExplicitFloorGrant?: boolean;
  };
}

export interface MeetingBotTestResult {
  ok: boolean;
  status: string;
  message: string;
  latencyMs: number | null;
  testedAt: string;
  steps: Array<{ name: string; ok: boolean; message: string; latencyMs: number | null }>;
}

export interface MeetingBotCapability {
  feature: 'teams_meeting_bot';
  enabled: boolean;
}

export function useMeetingBotFeature() {
  return useQuery<MeetingBotCapability>({
    queryKey: ['connections-module', 'meeting-bot', 'capability'],
    queryFn: () =>
      apiClient.get<MeetingBotCapability>(
        '/connections-module/meeting-bot/capability',
      ),
    retry: false,
    staleTime: 60_000,
  });
}

export function useMeetingBotConnections() {
  return useQuery<{ connections: MeetingBotConnection[] }, Error, MeetingBotConnection[]>({
    queryKey: ['connections-module', 'meeting-bot'],
    queryFn: () =>
      apiClient.get<{ connections: MeetingBotConnection[] }>(
        '/connections-module/meeting-bot',
      ),
    select: (r) => r.connections,
  });
}

export function useCreateMeetingBotConnection() {
  const qc = useQueryClient();
  return useMutation<MeetingBotConnection, Error, CreateMeetingBotConnectionInput>({
    mutationFn: (body) =>
      apiClient.post<MeetingBotConnection>('/connections-module/meeting-bot', body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['connections-module', 'meeting-bot'] }),
  });
}

export function useTestMeetingBotConnection() {
  const qc = useQueryClient();
  return useMutation<MeetingBotTestResult, Error, string>({
    mutationFn: (id) =>
      apiClient.post<MeetingBotTestResult>(
        `/connections-module/meeting-bot/${id}/test`,
        {},
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['connections-module', 'meeting-bot'] }),
  });
}

export function useDeleteMeetingBotConnection() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiClient.delete<void>(`/connections-module/meeting-bot/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['connections-module', 'meeting-bot'] }),
  });
}
